import { action, computed, observable } from 'mobx';

import { BusyStore } from 'ui/base/button';
import { BooleanStore, ChoiceStore } from 'ui/base/input';
import { HistoryModalStore, ModalStore } from 'ui/base/modal';

import { Optional, ResultKind } from 'services/base/types';
import { IAccountsDomain, Pangolin } from 'services/accounts/types';
import * as PangolinService from 'services/accounts/pangolin_service';

import { DeploymentProfile, getDeploymentSettingsProfile } from 'assets/enums/deployment_profiles/deployment_profiles';

export class InterceptTuningStore {

  private readonly pangolin: Pangolin;

  profileChoices: ChoiceStore<DeploymentProfile>;

  enablePhysicalIntercept: BooleanStore;
  enableUnicastSpoofing: BooleanStore;
  useUnicastRequest: BooleanStore;
  useUnicastResponse: BooleanStore;
  redirectInternetWithUnicast: BooleanStore;
  redirectInternalWithUnicast: BooleanStore;
  enableBroadcastSpoofing: BooleanStore;
  useBroadcastRequest: BooleanStore;
  useBroadcastResponse: BooleanStore;
  spoofGatewayWithBroadcast: BooleanStore;
  spoofHostsWithBroadcast: BooleanStore;
  masqueradeEgress: BooleanStore;

  profileModal: HistoryModalStore;

  enablePhysicalInterceptModal: ModalStore;
  enableUnicastSpoofingModal: ModalStore;
  useUnicastRequestModal: ModalStore;
  useUnicastResponseModal: ModalStore;
  redirectInternetWithUnicastModal: ModalStore;
  redirectInternalWithUnicastModal: ModalStore;
  enableBroadcastSpoofingModal: ModalStore;
  useBroadcastRequestModal: ModalStore;
  useBroadcastResponseModal: ModalStore;
  spoofGatewayWithBroadcastModal: ModalStore;
  spoofHostsWithBroadcastModal: ModalStore;
  masqueradeEgressModal: ModalStore;

  @observable.ref
  busy: BusyStore;

  constructor(pangolin: Pangolin) {
    this.pangolin = pangolin;

    this.profileChoices = new ChoiceStore<DeploymentProfile>({
      [DeploymentProfile.PASSIVE]: DeploymentProfile.PASSIVE,
      [DeploymentProfile.ACTIVE_FULL]: DeploymentProfile.ACTIVE_FULL,
      [DeploymentProfile.ACTIVE_INTERNET]: DeploymentProfile.ACTIVE_INTERNET,
    }, undefined);

    this.enablePhysicalIntercept = new BooleanStore(pangolin.redirectModeBridgeEnabled);
    this.enableUnicastSpoofing = new BooleanStore(pangolin.redirectModeUnicastEnabled);
    this.useUnicastRequest = new BooleanStore(pangolin.redirectModeUnicastTypeRequest);
    this.useUnicastResponse = new BooleanStore(pangolin.redirectModeUnicastTypeResponse);
    this.redirectInternetWithUnicast = new BooleanStore(pangolin.redirectModeUnicastNetworkInternet);
    this.redirectInternalWithUnicast = new BooleanStore(pangolin.redirectModeUnicastNetworkInternal);
    this.enableBroadcastSpoofing = new BooleanStore(pangolin.redirectModeBroadcastEnabled);
    this.useBroadcastRequest = new BooleanStore(pangolin.redirectModeBroadcastTypeRequest);
    this.useBroadcastResponse = new BooleanStore(pangolin.redirectModeBroadcastTypeResponse);
    this.spoofGatewayWithBroadcast = new BooleanStore(pangolin.redirectModeBroadcastHostGateways);
    this.spoofHostsWithBroadcast = new BooleanStore(pangolin.redirectModeBroadcastHostHosts);
    this.masqueradeEgress = new BooleanStore(pangolin.masqueradeEgress);

    this.profileModal = new HistoryModalStore(false);

    this.enablePhysicalInterceptModal = new ModalStore(false);
    this.enableUnicastSpoofingModal = new ModalStore(false);
    this.useUnicastRequestModal = new ModalStore(false);
    this.useUnicastResponseModal = new ModalStore(false);
    this.redirectInternetWithUnicastModal = new ModalStore(false);
    this.redirectInternalWithUnicastModal = new ModalStore(false);
    this.enableBroadcastSpoofingModal = new ModalStore(false);
    this.useBroadcastRequestModal = new ModalStore(false);
    this.useBroadcastResponseModal = new ModalStore(false);
    this.spoofGatewayWithBroadcastModal = new ModalStore(false);
    this.spoofHostsWithBroadcastModal = new ModalStore(false);
    this.masqueradeEgressModal = new ModalStore(false);

    this.busy = new BusyStore();
  }

  @action.bound
  async handleSave(accounts: IAccountsDomain): Promise<PangolinService.UpdatePangolinResult> {
    if (accounts.pangolins.has(this.pangolin.id)) {
      return await PangolinService.update({
        domain: accounts, id: this.pangolin.id,
        redirectModeBridgeEnabled: this.enablePhysicalIntercept.value,
        redirectModeUnicastEnabled: this.enableUnicastSpoofing.value,
        redirectModeUnicastTypeRequest: this.useUnicastRequest.value,
        redirectModeUnicastTypeResponse: this.useUnicastResponse.value,
        redirectModeUnicastNetworkInternet: this.redirectInternetWithUnicast.value,
        redirectModeUnicastNetworkInternal: this.redirectInternalWithUnicast.value,
        redirectModeBroadcastEnabled: this.enableBroadcastSpoofing.value,
        redirectModeBroadcastTypeRequest: this.useBroadcastRequest.value,
        redirectModeBroadcastTypeResponse: this.useBroadcastResponse.value,
        redirectModeBroadcastHostGateways: this.spoofGatewayWithBroadcast.value,
        redirectModeBroadcastHostHosts: this.spoofHostsWithBroadcast.value,
        masqueradeEgress: this.masqueradeEgress.value,
      });
    }

    return { kind: ResultKind.SYSTEM_ERROR };
  }

  @computed
  get settingsProfile(): Optional<DeploymentProfile> {
    return getDeploymentSettingsProfile({
      enablePhysicalIntercept: this.enablePhysicalIntercept.value,
      enableUnicastSpoofing: this.enableUnicastSpoofing.value,
      useUnicastRequest: this.useUnicastRequest.value,
      useUnicastResponse: this.useUnicastResponse.value,
      redirectInternetWithUnicast: this.redirectInternetWithUnicast.value,
      redirectInternalWithUnicast: this.redirectInternalWithUnicast.value,
      enableBroadcastSpoofing: this.enableBroadcastSpoofing.value,
      useBroadcastRequest: this.useBroadcastRequest.value,
      useBroadcastResponse: this.useBroadcastResponse.value,
      spoofGatewayWithBroadcast: this.spoofGatewayWithBroadcast.value,
      spoofHostsWithBroadcast: this.spoofHostsWithBroadcast.value,
      masqueradeEgress: this.masqueradeEgress.value,
    });
  }
}
